import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Category as CategoryIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  SubdirectoryArrowLeft as SubIcon,
  Article as ArticleCountIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as categoryService from '../services/articleCategoryService';
import { ConfirmDialog } from '../components/common';

// ==================== TYPES ====================

interface CategoryNode extends categoryService.ArticleCategory {
  children: CategoryNode[];
  isExpanded?: boolean;
}

// ==================== HELPERS ====================

/**
 * Build tree from flat categories list
 */
const buildCategoryTree = (categories: categoryService.ArticleCategory[]): CategoryNode[] => {
  const map = new Map<string, CategoryNode>();
  const roots: CategoryNode[] = [];

  // First pass: create nodes
  categories.forEach((cat) => {
    map.set(cat._id, { ...cat, children: [], isExpanded: true });
  });

  // Second pass: build tree using parentId from backend
  categories.forEach((cat) => {
    const node = map.get(cat._id)!;
    const catParentId = cat.parentId ? String(cat.parentId) : null;
    if (catParentId) {
      const parent = map.get(catParentId);
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  // Sort by order
  const sortByOrder = (items: CategoryNode[]) => {
    items.sort((a, b) => a.order - b.order);
    items.forEach((item) => sortByOrder(item.children));
  };
  sortByOrder(roots);

  return roots;
};

/**
 * Flatten tree for parent selection dropdown (excluding a specific node and its children)
 */
const flattenForSelect = (
  nodes: CategoryNode[],
  excludeId?: string,
  level: number = 0,
  maxLevel: number = 2
): Array<{ _id: string; name: string; level: number }> => {
  if (level >= maxLevel) return [];
  const result: Array<{ _id: string; name: string; level: number }> = [];
  nodes.forEach((node) => {
    if (node._id === excludeId) return;
    result.push({ _id: node._id, name: node.name, level });
    result.push(...flattenForSelect(node.children, excludeId, level + 1, maxLevel));
  });
  return result;
};

// ==================== MAIN COMPONENT ====================

const ArticleCategoryManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Data
  const [categories, setCategories] = useState<categoryService.ArticleCategory[]>([]);
  const [categoryTree, setCategoryTree] = useState<CategoryNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<categoryService.ArticleCategory | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<categoryService.ArticleCategory | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formIcon, setFormIcon] = useState('');
  const [formParent, setFormParent] = useState<string>('');
  const [formOrder, setFormOrder] = useState(0);
  const [formIsActive, setFormIsActive] = useState(true);

  // Expanded nodes tracking
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Load categories
  const loadCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await categoryService.getCategories();
      const cats = Array.isArray(data) ? data : [];
      setCategories(cats);
      setCategoryTree(buildCategoryTree(cats));
      // Expand all by default
      setExpandedNodes(new Set(cats.map((c) => c._id)));
    } catch (err: any) {
      setError(err.message || 'خطا در بارگذاری دسته‌بندی‌ها');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // ==================== DIALOG HANDLERS ====================

  const handleOpenCreate = (parentId?: string) => {
    setEditingCategory(null);
    setFormName('');
    setFormDescription('');
    setFormIcon('');
    setFormParent(parentId || '');
    setFormOrder(0);
    setFormIsActive(true);
    setDialogOpen(true);
  };

  const handleOpenEdit = (category: categoryService.ArticleCategory) => {
    setEditingCategory(category);
    setFormName(category.name);
    setFormDescription(category.description || '');
    setFormIcon(category.icon || '');
    setFormParent(category.parentId ? String(category.parentId) : '');
    setFormOrder(category.order);
    setFormIsActive(category.isActive);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
  };

  const handleSubmit = async () => {
    if (!formName.trim()) {
      enqueueSnackbar('نام دسته‌بندی الزامی است', { variant: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const data: categoryService.CategoryFormData = {
        name: formName.trim(),
        description: formDescription.trim() || undefined,
        icon: formIcon.trim() || undefined,
        parentId: formParent || null,
        order: formOrder,
        isActive: formIsActive,
      };

      if (editingCategory) {
        await categoryService.updateCategory(editingCategory._id, data);
        enqueueSnackbar('دسته‌بندی با موفقیت ویرایش شد', { variant: 'success' });
      } else {
        await categoryService.createCategory(data);
        enqueueSnackbar('دسته‌بندی با موفقیت ایجاد شد', { variant: 'success' });
      }

      handleCloseDialog();
      loadCategories();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در ذخیره دسته‌بندی', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (category: categoryService.ArticleCategory) => {
    setDeletingCategory(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingCategory) return;
    setSubmitting(true);
    try {
      await categoryService.deleteCategory(deletingCategory._id);
      enqueueSnackbar('دسته‌بندی با موفقیت حذف شد', { variant: 'success' });
      setDeleteDialogOpen(false);
      setDeletingCategory(null);
      loadCategories();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در حذف دسته‌بندی', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExpand = (id: string) => {
    const next = new Set(expandedNodes);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setExpandedNodes(next);
  };

  // ==================== TREE RENDERER ====================

  const renderCategoryNode = (node: CategoryNode, level: number = 0) => {
    const isExpanded = expandedNodes.has(node._id);
    const hasChildren = node.children.length > 0;

    return (
      <React.Fragment key={node._id}>
        <ListItem
          sx={{
            pl: 2 + level * 4,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {/* Expand/Collapse */}
          <ListItemIcon sx={{ minWidth: 36 }}>
            {hasChildren ? (
              <IconButton size="small" onClick={() => toggleExpand(node._id)}>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            ) : (
              <Box sx={{ width: 36 }} />
            )}
          </ListItemIcon>

          {/* Folder icon */}
          <ListItemIcon sx={{ minWidth: 36 }}>
            {hasChildren && isExpanded ? (
              <FolderOpenIcon color="primary" />
            ) : (
              <FolderIcon color={level === 0 ? 'primary' : level === 1 ? 'secondary' : 'action'} />
            )}
          </ListItemIcon>

          {/* Content */}
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {node.icon && (
                  <Typography component="span" sx={{ fontSize: 18 }}>
                    {node.icon}
                  </Typography>
                )}
                <Typography fontWeight={level === 0 ? 700 : 500}>
                  {node.name}
                </Typography>
                {!node.isActive && (
                  <Chip label="غیرفعال" size="small" color="default" />
                )}
                <Chip
                  icon={<ArticleCountIcon sx={{ fontSize: '14px !important' }} />}
                  label={`${node.articleCount || 0} مقاله`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            }
            secondary={
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                {node.description && (
                  <Typography variant="caption" color="text.secondary">
                    {node.description}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  ترتیب: {node.order}
                </Typography>
              </Box>
            }
          />

          {/* Actions */}
          <ListItemSecondaryAction>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {/* Add sub-category (only if level < 2) */}
              {level < 2 && (
                <Tooltip title="افزودن زیرمجموعه">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenCreate(node._id)}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="ویرایش">
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => handleOpenEdit(node)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="حذف">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteClick(node)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </ListItemSecondaryAction>
        </ListItem>

        {/* Children */}
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            {node.children.map((child) => renderCategoryNode(child, level + 1))}
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  // Parent options for dropdown
  const parentOptions = flattenForSelect(
    categoryTree,
    editingCategory?._id,
    0,
    2
  );

  // ==================== RENDER ====================

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          مدیریت دسته‌بندی مقالات
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={loadCategories} disabled={loading}>
            بروزرسانی
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenCreate()}
          >
            دسته‌بندی جدید
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        دسته‌بندی‌ها تا ۳ سطح عمق پشتیبانی می‌شوند. برای افزودن زیرمجموعه، از دکمه + کنار هر دسته استفاده کنید.
      </Alert>

      {/* Category Tree */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : categoryTree.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">دسته‌بندی‌ای یافت نشد</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {categoryTree.map((node) => renderCategoryNode(node, 0))}
          </List>
        )}
      </Paper>

      {/* ==================== ADD/EDIT DIALOG ==================== */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCategory ? 'ویرایش دسته‌بندی' : 'ایجاد دسته‌بندی جدید'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="نام دسته‌بندی"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            required
            sx={{ mt: 1, mb: 2 }}
            autoFocus
          />
          <TextField
            fullWidth
            label="توضیحات (اختیاری)"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="آیکون (اختیاری - ایموجی یا نام آیکون)"
            value={formIcon}
            onChange={(e) => setFormIcon(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="مثلا: 🎮"
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>دسته والد</InputLabel>
            <Select
              value={formParent}
              label="دسته والد"
              onChange={(e) => setFormParent(e.target.value)}
            >
              <MenuItem value="">بدون والد (دسته اصلی)</MenuItem>
              {parentOptions.map((opt) => (
                <MenuItem key={opt._id} value={opt._id}>
                  {'─'.repeat(opt.level)} {opt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="ترتیب نمایش"
            type="number"
            value={formOrder}
            onChange={(e) => setFormOrder(parseInt(e.target.value, 10) || 0)}
            sx={{ mb: 2 }}
            inputProps={{ min: 0 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formIsActive}
                onChange={(e) => setFormIsActive(e.target.checked)}
              />
            }
            label="فعال"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>انصراف</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !formName.trim()}
            startIcon={submitting ? <CircularProgress size={18} /> : undefined}
          >
            {editingCategory ? 'ذخیره تغییرات' : 'ایجاد'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== DELETE DIALOG ==================== */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="حذف دسته‌بندی"
        message={`آیا از حذف دسته‌بندی «${deletingCategory?.name}» اطمینان دارید؟`}
        confirmText="حذف"
        cancelText="انصراف"
        confirmColor="error"
        loading={submitting}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setDeletingCategory(null);
        }}
        onConfirm={handleDeleteConfirm}
      >
        {deletingCategory && (deletingCategory as any).children?.length > 0 && (
          <Alert severity="error">
            این دسته‌بندی زیرمجموعه دارد. ابتدا زیرمجموعه‌ها را حذف یا جابجا کنید.
          </Alert>
        )}
        {deletingCategory && (deletingCategory.articleCount || 0) > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            این دسته‌بندی {deletingCategory.articleCount} مقاله دارد. با حذف، مقالات بدون دسته‌بندی خواهند شد.
          </Alert>
        )}
      </ConfirmDialog>
    </Container>
  );
};

export default ArticleCategoryManagement;
